<template>
  <div>
    <!-- Section header -->
    <div class="mb-6">
      <div class="ext-section-number">Coding</div>
      <h1 class="ext-section-title">HCC Coding Gaps</h1>
      <p class="ext-section-desc">Open Hierarchical Condition Category gaps for attributed patients. Each row represents a diagnosis that was documented in a prior year but has not yet been recaptured in the current performance year. Gaps are grouped by patient — patients with more open gaps represent higher risk adjustment opportunity.</p>
    </div>

    <!-- Charts row: trend + prevalence pie -->
    <div class="coding-charts-row mb-6">
      <div class="coding-chart-half">
        <div v-if="chartEntityName" style="display:flex; align-items:center; gap:8px; margin-bottom:6px;">
          <button @click="chartEntityId = null; chartEntityName = null"
            style="font-size:11px; color:#6B7280; border:1px solid #D1D5DB; border-radius:4px; padding:2px 8px; cursor:pointer;">
            ← All Providers
          </button>
          <span class="sensitive" style="font-size:12px; color:#1e293b; font-weight:500;">{{ chartEntityName }}</span>
        </div>
        <PerformanceTrend
          :key="chartEntityId || entityId"
          :metric-ids="[607]"
          :year="2026"
          :entity-id="chartEntityId || entityId"
        />
      </div>
      <div class="coding-chart-half coding-pie-container">
        <h3 style="font-size: 13px; font-weight: 600; color: #221F1C; margin-bottom: 8px;">Open Gaps by HCC</h3>
        <div v-if="!loading && rowData.length" ref="pieWrap" style="flex: 1; display: flex; align-items: center; gap: 16px; padding: 12px 20px;">
          <div style="flex-shrink: 0; position: relative;">
            <canvas ref="pieCanvas"></canvas>
            <div v-if="pieTooltip" class="pie-tooltip" :style="{ left: pieTooltip.x + 'px', top: pieTooltip.y + 'px' }">
              <strong class="sensitive">{{ pieTooltip.desc }}</strong><br>
              {{ pieTooltip.patients.toLocaleString() }} patients ({{ pieTooltip.pct }}%)
            </div>
          </div>
          <div class="pie-legend">
            <div v-for="s in pieSlices" :key="s.hcc" class="pie-legend-row" :title="s.hcc + ' — ' + s.desc">
              <span class="pie-legend-swatch" :style="{ background: s.color }"></span>
              <span class="pie-legend-label">{{ s.desc }}</span>
              <span class="pie-legend-value sensitive">{{ s.patients.toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">Loading HCC gaps…</div>
    <div v-else-if="!rowData.length" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">No HCC gap data available.</div>

    <!-- PCP Summary Table (default view) -->
    <template v-else-if="!selectedPcp">
      <p class="text-xs text-gray-400 mb-2">Click a provider row to see their individual coding gaps.</p>
      <div class="coding-filters">
        <div class="coding-filter-group">
          <label class="coding-filter-label">Care Setting</label>
          <select v-model="careSetting" class="coding-select">
            <option value="">All</option>
            <option value="SNF">SNF</option>
            <option value="ALF">ALF</option>
          </select>
        </div>
        <div class="coding-filter-count sensitive">{{ pcpSummary.length }} providers · {{ rowData.length.toLocaleString() }} total gaps</div>
        <ExportMenu :disabled="!rowData.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
      <div class="ext-table-wrap" style="overflow: auto; max-height: calc(100vh - 400px);">
        <table class="ext-table" style="white-space: nowrap;">
          <thead>
            <tr>
              <th v-for="col in pcpColumns" :key="col.key"
                  :class="col.align === 'right' ? 'num' : ''"
                  :style="col.key === 'pcp' ? 'padding-left: 16px; cursor: pointer; user-select: none;' : 'cursor: pointer; user-select: none;'"
                  @click="togglePcpSort(col.key)"
              >
                {{ col.label }}
                <span class="coding-sort sensitive">{{ pcpSortIcon(col.key) }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedPcpSummary" :key="row.pcp"
                class="cursor-pointer"
                style="transition: background 0.1s;"
                @mouseenter="$event.currentTarget.style.background='#F9F6F1'"
                @mouseleave="$event.currentTarget.style.background=''"
                @click="onPcpClick(row)"
            >
              <td style="padding-left: 16px;">
                <span class="sensitive" style="font-weight: 500; color: #221F1C; display: inline-flex; align-items: center; gap: 4px;">
                  {{ row.pcp }}
                  <font-awesome-icon :icon="['fas', 'chevron-right']" style="font-size: 8px; color: #A9A29A;" />
                </span>
              </td>
              <td class="num sensitive">{{ row.patients.toLocaleString() }}</td>
              <td class="num">{{ row.totalGaps.toLocaleString() }}</td>
              <td class="num">{{ row.uniqueHccs }}</td>
              <td class="num">{{ row.avgGaps }}</td>
              <td class="num">{{ row.facilities }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Gap Detail Table (drilldown view) -->
    <template v-else>
      <div class="coding-filters" style="margin-bottom: 8px;">
        <button
          class="coding-export"
          style="border-color: #CFC8BE;"
          @click="selectedPcp = null; searchText = ''; selectedFacility = null; selectedHcc = null; chartEntityId = null; chartEntityName = null;"
        >
          <span style="font-size: 10px;">←</span> All Providers
        </button>
        <span class="sensitive" style="font-size: 12px; font-weight: 500; color: #221F1C;">{{ selectedPcp }}</span>
        <span style="font-size: 11px; color: #A9A29A;">· {{ filteredRows.length.toLocaleString() }} gaps</span>
      </div>
      <div class="coding-filters">
        <div class="coding-filter-group">
          <input v-model="searchText" type="text" placeholder="Search patient name or MBI…" class="coding-input" />
        </div>
        <div class="coding-filter-group">
          <label class="coding-filter-label">Facility</label>
          <select v-model="selectedFacility" class="coding-select">
            <option :value="null">All Facilities</option>
            <option v-for="f in facilityOptions" :key="f.name" :value="f.name">{{ f.name }} ({{ f.count }})</option>
          </select>
        </div>
        <div class="coding-filter-group">
          <label class="coding-filter-label">HCC</label>
          <select v-model="selectedHcc" class="coding-select">
            <option :value="null">All HCCs</option>
            <option v-for="h in hccOptions" :key="h.hcc" :value="h.hcc">{{ h.hcc }} — {{ h.desc }} ({{ h.count }})</option>
          </select>
        </div>
        <div class="coding-filter-count">{{ sortedRows.length.toLocaleString() }} gaps</div>
        <ExportMenu :disabled="!sortedRows.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
      <div v-if="sortedRows.length" class="ext-table-wrap" style="overflow: auto; max-height: calc(100vh - 420px);">
        <table class="ext-table" style="white-space: nowrap;">
          <thead>
            <tr>
              <th style="padding-left: 16px; cursor: pointer; user-select: none;" @click="toggleSort('last_name')">Patient <span class="coding-sort">{{ sortIcon('last_name') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('dob')">DOB <span class="coding-sort sensitive">{{ sortIcon('dob') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('patient_status')">Status <span class="coding-sort sensitive">{{ sortIcon('patient_status') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('patient_facility')">Facility <span class="coding-sort sensitive">{{ sortIcon('patient_facility') }}</span></th>
              <th class="num" style="cursor: pointer; user-select: none;" @click="toggleSort('hcc')">HCC <span class="coding-sort">{{ sortIcon('hcc') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('icd10')">ICD-10 <span class="coding-sort">{{ sortIcon('icd10') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('description')">Description <span class="coding-sort">{{ sortIcon('description') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('prior_billed_date')">Last Billed <span class="coding-sort">{{ sortIcon('prior_billed_date') }}</span></th>
              <th style="cursor: pointer; user-select: none;" @click="toggleSort('prior_billed_provider')">Billed By <span class="coding-sort">{{ sortIcon('prior_billed_provider') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in sortedRows" :key="i">
              <td style="padding-left: 16px;">
                <div style="font-weight: 500; color: #221F1C;">{{ row.last_name }}, {{ row.first_name }}</div>
                <div class="sensitive" style="font-size: 10px; color: #A9A29A; font-family: 'JetBrains Mono', monospace; margin-top: 1px;">{{ row.mbi }}</div>
              </td>
              <td class="num sensitive" style="font-size: 11px;">{{ formatDate(row.dob) }}</td>
              <td class="sensitive" style="font-size: 11px;">{{ row.patient_status || 'Active' }}</td>
              <td class="sensitive" style="font-size: 11px;">{{ row.patient_facility || '—' }}</td>
              <td class="num">{{ row.hcc }}</td>
              <td style="font-family: 'JetBrains Mono', monospace; font-size: 10.5px;">{{ row.icd10 }}</td>
              <td>{{ row.description }}</td>
              <td class="num" style="font-size: 11px;">{{ formatDate(row.prior_billed_date) }}</td>
              <td style="font-size: 11px;">{{ row.prior_billed_provider || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else style="padding: 20px; text-align: center; color: #A9A29A; font-size: 13px;">No gaps match filters.</div>
    </template>
  </div>
</template>

<script>
import PerformanceTrend from '@/components/InsightsNew/PerformanceTrend.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

export default {
  name: 'ExternalCoding',
  components: { PerformanceTrend, ExportMenu },
  props: {
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      rowData: [],
      selectedPcp: null,
      chartEntityId: null,
      chartEntityName: null,
      sortKey: 'last_name',
      sortDir: 1,
      pcpSortKey: 'totalGaps',
      pcpSortDir: -1,
      searchText: '',
      selectedFacility: null,
      selectedHcc: null,
      careSetting: '',
      pieTooltip: null,
    }
  },
  computed: {
    pcpColumns() {
      return [
        { key: 'pcp', label: 'Provider' },
        { key: 'patients', label: 'Patients', align: 'right' },
        { key: 'totalGaps', label: 'Open Gaps', align: 'right' },
        { key: 'uniqueHccs', label: 'HCCs', align: 'right' },
        { key: 'avgGaps', label: 'Avg/Pt', align: 'right' },
        { key: 'facilities', label: 'Facilities', align: 'right' },
      ]
    },
    pieSlices() {
      const map = {}
      this.rowData.forEach(r => {
        if (r.hcc == null) return
        if (!map[r.hcc]) map[r.hcc] = { hcc: String(r.hcc), desc: r.description || '', patients: new Set() }
        if (r.mbi) map[r.hcc].patients.add(r.mbi)
      })
      const all = Object.values(map)
        .map(b => ({ hcc: b.hcc, desc: b.desc, patients: b.patients.size }))
        .filter(b => b.patients > 0)
        .sort((a, b) => b.patients - a.patients)
      const colors = [
        '#1e40af','#2563eb','#3b82f6','#60a5fa','#93c5fd',
        '#1d4ed8','#4b7ab8','#5b8cc9','#7faed4','#a3c4e0',
        '#3d5a80','#4b6584','#5a7088','#697b8c','#788690',
      ]
      // Top 10 + "Other"
      const top = all.slice(0, 10)
      const rest = all.slice(10)
      if (rest.length) {
        const otherPts = rest.reduce((s, r) => s + r.patients, 0)
        top.push({ hcc: '—', desc: 'Other', patients: otherPts })
      }
      return top.map((s, i) => ({ ...s, color: i < colors.length ? colors[i] : '#B0ADA8' }))
    },
    pcpSummary() {
      const map = {};
      this.rowData.forEach(r => {
        const name = r.pcp_name || '(No PCP)';
        if (!map[name]) map[name] = { pcp: name, npi: null, patients: new Set(), totalGaps: 0, hccs: new Set(), facilities: new Set() };
        const s = map[name];
        if (r.npi && !s.npi) s.npi = r.npi;
        if (r.mbi) s.patients.add(r.mbi);
        s.totalGaps++;
        if (r.hcc) s.hccs.add(r.hcc);
        if (r.patient_facility) s.facilities.add(r.patient_facility);
      });
      return Object.values(map).map(s => ({
        pcp: s.pcp,
        npi: s.npi,
        patients: s.patients.size,
        totalGaps: s.totalGaps,
        uniqueHccs: s.hccs.size,
        avgGaps: s.patients.size ? (s.totalGaps / s.patients.size).toFixed(1) : '0',
        facilities: s.facilities.size,
      }));
    },
    sortedPcpSummary() {
      const rows = [...this.pcpSummary];
      const key = this.pcpSortKey;
      const dir = this.pcpSortDir;
      rows.sort((a, b) => {
        let av = a[key], bv = b[key];
        if (typeof av === 'string') return av.localeCompare(bv) * dir;
        return ((av || 0) - (bv || 0)) * dir;
      });
      return rows;
    },
    pcpFilteredRows() {
      if (!this.selectedPcp) return this.rowData;
      return this.rowData.filter(r => (r.pcp_name || '(No PCP)') === this.selectedPcp);
    },
    facilityOptions() {
      const map = {};
      this.pcpFilteredRows.forEach(r => {
        const f = r.patient_facility || '(Unknown)';
        map[f] = (map[f] || 0) + 1;
      });
      return Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    },
    hccOptions() {
      const map = {};
      this.pcpFilteredRows.forEach(r => {
        if (r.hcc) {
          if (!map[r.hcc]) map[r.hcc] = { hcc: r.hcc, desc: r.description || '', count: 0 };
          map[r.hcc].count++;
        }
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    },
    filteredRows() {
      let rows = this.pcpFilteredRows;
      if (this.searchText) {
        const q = this.searchText.toLowerCase();
        rows = rows.filter(r =>
          (r.last_name || '').toLowerCase().includes(q) ||
          (r.first_name || '').toLowerCase().includes(q) ||
          (r.mbi || '').toLowerCase().includes(q)
        );
      }
      if (this.selectedFacility) rows = rows.filter(r => (r.patient_facility || '(Unknown)') === this.selectedFacility);
      if (this.selectedHcc) rows = rows.filter(r => r.hcc === this.selectedHcc);
      return rows;
    },
    sortedRows() {
      const key = this.sortKey;
      const dir = this.sortDir;
      return [...this.filteredRows].sort((a, b) => {
        let av, bv;
        if (key === 'last_name') { av = (a.last_name || '').toLowerCase(); bv = (b.last_name || '').toLowerCase() }
        else if (key === 'dob') { av = a.dob || ''; bv = b.dob || '' }
        else if (key === 'patient_status') { av = a.patient_status || 'Active'; bv = b.patient_status || 'Active' }
        else if (key === 'patient_facility') { av = (a.patient_facility || '').toLowerCase(); bv = (b.patient_facility || '').toLowerCase() }
        else if (key === 'hcc') { av = a.hcc || 0; bv = b.hcc || 0 }
        else if (key === 'icd10') { av = a.icd10 || ''; bv = b.icd10 || '' }
        else if (key === 'prior_billed_date') { av = a.prior_billed_date || ''; bv = b.prior_billed_date || '' }
        else if (key === 'prior_billed_provider') { av = (a.prior_billed_provider || '').toLowerCase(); bv = (b.prior_billed_provider || '').toLowerCase() }
        else { av = (a.description || '').toLowerCase(); bv = (b.description || '').toLowerCase() }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
  },
  watch: {
    careSetting() { this.loadData(); },
    entityId() { this.loadData(); },
    pieSlices() { this.$nextTick(() => this.drawPie()) },
  },
  mounted() { this.loadData() },
  beforeUnmount() {
    const canvas = this.$refs.pieCanvas
    if (canvas && this._pieHandlerBound) {
      canvas.removeEventListener('mousemove', this._onPieMouseMove)
      canvas.removeEventListener('mouseleave', this._onPieMouseLeave)
    }
  },
  methods: {
    drawPie() {
      const canvas = this.$refs.pieCanvas
      const wrap = this.$refs.pieWrap
      if (!canvas || !wrap || !this.pieSlices.length) return
      const ctx = canvas.getContext('2d')
      const dpr = window.devicePixelRatio || 1
      const size = Math.round(Math.min(wrap.clientWidth * 0.36, wrap.clientHeight - 24))
      if (size <= 0) return
      canvas.width = size * dpr
      canvas.height = size * dpr
      canvas.style.width = size + 'px'
      canvas.style.height = size + 'px'
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, size, size)

      const cx = size / 2, cy = size / 2, r = size / 2 - 4
      const total = this.pieSlices.reduce((s, d) => s + d.patients, 0)

      // Store slice angles for hit testing
      this._pieGeom = { cx, cy, r, innerR: r * 0.45, total, slices: [] }
      let angle = -Math.PI / 2
      for (const s of this.pieSlices) {
        const sweep = (s.patients / total) * Math.PI * 2
        const startAngle = angle
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, r, angle, angle + sweep)
        ctx.closePath()
        ctx.fillStyle = s.color
        ctx.fill()
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 1.5
        ctx.stroke()
        this._pieGeom.slices.push({ ...s, startAngle, endAngle: angle + sweep, pct: Math.round(s.patients / total * 100) })
        angle += sweep
      }
      // Donut hole
      ctx.beginPath()
      ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2)
      ctx.fillStyle = '#fff'
      ctx.fill()
      // Center label
      ctx.fillStyle = '#221F1C'
      ctx.font = `600 ${Math.round(size * 0.075)}px "JetBrains Mono", monospace`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(total.toLocaleString(), cx, cy - size * 0.025)
      ctx.fillStyle = '#A9A29A'
      ctx.font = `500 ${Math.round(size * 0.04)}px system-ui, sans-serif`
      ctx.fillText('open gaps', cx, cy + size * 0.05)

      // Attach mouse handlers once
      if (!this._pieHandlerBound) {
        this._onPieMouseLeave = () => { this.pieTooltip = null }
        canvas.addEventListener('mousemove', this._onPieMouseMove)
        canvas.addEventListener('mouseleave', this._onPieMouseLeave)
        this._pieHandlerBound = true
      }
    },
    _onPieMouseMove(e) {
      const canvas = this.$refs.pieCanvas
      if (!canvas || !this._pieGeom) return
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const scaleX = canvas.clientWidth / (canvas.width / (window.devicePixelRatio || 1))
      const { cx, cy, r, innerR, slices } = this._pieGeom
      const dx = x - cx, dy = y - cy
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < innerR || dist > r) { this.pieTooltip = null; return }
      let angle = Math.atan2(dy, dx)
      if (angle < -Math.PI / 2) angle += Math.PI * 2
      const hit = slices.find(s => angle >= s.startAngle && angle < s.endAngle)
      if (hit) {
        this.pieTooltip = { desc: hit.desc, patients: hit.patients, pct: hit.pct, x: x + 12, y: y - 10 }
      } else {
        this.pieTooltip = null
      }
    },
    async onPcpClick(row) {
      this.selectedPcp = row.pcp
      // Resolve NPI → entity ID for chart scoping
      if (row.npi) {
        try {
          const resp = await this.$axios.get('/api/identifiers/', {
            params: {
              identifier_type_id: 11,
              search: String(row.npi),
            },
          })
          const results = resp.data?.results || resp.data || []
          if (results.length) {
            this.chartEntityId = results[0].entity
            this.chartEntityName = row.pcp
          }
        } catch (e) {
          // Couldn't resolve — chart stays at parent entity
        }
      }
    },
    togglePcpSort(key) {
      if (this.pcpSortKey === key) { this.pcpSortDir *= -1 }
      else { this.pcpSortKey = key; this.pcpSortDir = key === 'pcp' ? 1 : -1 }
    },
    pcpSortIcon(key) {
      if (this.pcpSortKey !== key) return '↕';
      return this.pcpSortDir === 1 ? '↑' : '↓';
    },
    toggleSort(key) {
      if (this.sortKey === key) { this.sortDir *= -1 }
      else { this.sortKey = key; this.sortDir = 1 }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return '↕';
      return this.sortDir === 1 ? '↑' : '↓';
    },
    formatDate(d) {
      if (!d) return '—';
      const s = String(d).slice(0, 10);
      const parts = s.split('-');
      return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : s;
    },
    async loadData() {
      this.loading = true;
      this.selectedPcp = null;
      try {
        const resp = await this.$axios.post('/api/portfolio_management/cclf-report/hcc-gaps/', {
          contract_id: 'A5069',
          performance_year: 2026,
          entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
        });
        this.rowData = (resp.data || []).map(r => ({
          mbi: r.mbi || r.bene_mbi_id,
          first_name: r.first_name || r.bene_first_name,
          last_name: r.last_name || r.bene_last_name,
          dob: r.dob || r.bene_dob,
          patient_status: r.patient_status,
          patient_facility: r.patient_facility || r.facility_name,
          pcp_name: r.pcp_name || r.provider_name || '(No PCP)',
          npi: r.npi,
          hcc: r.hcc || r.hcc_code,
          icd10: r.icd10 || r.icd10_code,
          description: r.description || r.hcc_description,
          prior_billed_date: r.prior_billed_date,
          prior_billed_provider: r.prior_billed_provider,
          prior_billed_npi: r.prior_billed_npi,
          dx_description: r.dx_description,
        }));
      } catch (e) {
        console.error('Failed to load HCC gaps:', e);
        this.rowData = [];
      } finally {
        this.loading = false;
      }
    },
    exportCsv() {
      const headers = ['Patient', 'MBI', 'DOB', 'Status', 'Facility', 'Provider', 'HCC', 'ICD-10', 'HCC Description', 'DX Description', 'Last Billed', 'Billed By'];
      const source = this.selectedPcp ? this.sortedRows : this.rowData;
      const rows = source.map(r => [
        `${r.last_name}, ${r.first_name}`, r.mbi, r.dob, r.patient_status || 'Active',
        r.patient_facility || '', r.pcp_name || '', r.hcc, r.icd10, r.description,
        r.dx_description || '', r.prior_billed_date || '', r.prior_billed_provider || '',
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hcc_coding_gaps.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  },
}
</script>

<style scoped>
.coding-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.coding-filter-group {
  display: flex;
  align-items: center;
  gap: 4px;
}
.coding-filter-label {
  font-size: 11px;
  color: #6B7280;
  white-space: nowrap;
}
.coding-filter-count {
  font-size: 11px;
  color: #A9A29A;
  margin-left: auto;
}
.coding-input {
  padding: 5px 10px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 12px;
  width: 200px;
}
.coding-select {
  padding: 5px 8px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 12px;
  max-width: 260px;
}
.coding-export {
  padding: 4px 12px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 11px;
  color: #374151;
  background: #fff;
  cursor: pointer;
}
.coding-export:hover { background: #F9FAFB; }
.coding-charts-row {
  display: flex;
  gap: 20px;
  align-items: stretch;
}
.coding-chart-half {
  flex: 1;
  min-width: 0;
}
.coding-pie-container {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.coding-pie-container h3 {
  margin: 0;
  padding: 12px 16px;
  background: #eff6ff;
  border-bottom: 1px solid #dbeafe;
  border-radius: 8px 8px 0 0;
}
.pie-legend {
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}
.pie-legend-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 1.3;
}
.pie-legend-swatch {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
.pie-legend-label {
  color: #4B4843;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.pie-legend-value {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: #7D7770;
  flex-shrink: 0;
}
.pie-tooltip {
  position: absolute;
  background: #1e293b;
  color: #fff;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 6px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.coding-sort {
  font-size: 9px;
  color: #A9A29A;
  margin-left: 2px;
}
</style>
